import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Package, IndianRupee, Bell, Plus, LogOut, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminMe, adminGetStats, adminGetOrders, adminUpdateOrderStatus,
  adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminGetSubscribers, fetchProducts, inr, waLink,
  fetchOfferBanner, adminSaveOfferBanner, fetchAnnouncementBar, adminSaveAnnouncementBar,
  fetchHero, adminSaveHero, fetchHomeVideo, adminSaveHomeVideo,
} from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ProductForm } from "../../components/admin/ProductForm";

const ORDER_STATUSES = ["placed", "confirmed", "shipped", "delivered", "cancelled"];

const STATUS_STYLE = {
  placed: "bg-gold/15 text-gold-dark",
  confirmed: "bg-blue-50 text-blue-700",
  shipped: "bg-purple-50 text-purple-700",
  delivered: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-700",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("orders");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [offerText, setOfferText] = useState("");
  const [savingOffer, setSavingOffer] = useState(false);
  const [announcementText, setAnnouncementText] = useState("");
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [heroForm, setHeroForm] = useState({ overline: "", line1: "", line2: "", subheadline: "" });
  const [savingHero, setSavingHero] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);

  const loadAll = useCallback(async () => {
    const [s, o, p, subs, offer, ann, hero, vid] = await Promise.all([adminGetStats(), adminGetOrders(), fetchProducts({ limit: 500 }), adminGetSubscribers(), fetchOfferBanner(), fetchAnnouncementBar(), fetchHero(), fetchHomeVideo()]);
    setStats(s);
    setOrders(o);
    setProducts(p);
    setSubscribers(subs);
    setOfferText((offer?.items || []).join("\n"));
    setAnnouncementText((ann?.items || []).join("\n"));
    setHeroForm({ overline: hero?.overline || "", line1: hero?.line1 || "", line2: hero?.line2 || "", subheadline: hero?.subheadline || "" });
    setVideoUrl(vid?.url || "");
  }, []);

  useEffect(() => {
    adminMe()
      .then((user) => {
        setMe(user);
        loadAll().catch(() => toast.error("Could not load dashboard data"));
      })
      .catch(() => {
        localStorage.removeItem("rvi_admin_token");
        navigate("/admin/login");
      });
  }, [navigate, loadAll]);

  const logout = () => {
    localStorage.removeItem("rvi_admin_token");
    navigate("/admin/login");
  };

  const changeStatus = async (id, status) => {
    try {
      await adminUpdateOrderStatus(id, status);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Order ${id} marked ${status}`);
    } catch {
      toast.error("Could not update order");
    }
  };

  const saveProduct = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await adminUpdateProduct(editing.id, payload);
        toast.success("Saree updated");
      } else {
        await adminCreateProduct(payload);
        toast.success("Saree added to your store");
      }
      setFormOpen(false);
      setEditing(null);
      await loadAll();
    } catch {
      toast.error("Could not save the saree");
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the store?`)) return;
    try {
      await adminDeleteProduct(p.id);
      toast.success("Saree removed");
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
    } catch {
      toast.error("Could not delete");
    }
  };

  const saveOffer = async () => {
    const items = offerText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!items.length) return toast.error("Add at least one offer line");
    setSavingOffer(true);
    try {
      await adminSaveOfferBanner(items);
      toast.success("Offer banner updated — live on the home page");
    } catch {
      toast.error("Could not save the offer banner");
    } finally {
      setSavingOffer(false);
    }
  };

  const saveAnnouncement = async () => {
    const items = announcementText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!items.length) return toast.error("Add at least one announcement line");
    setSavingAnnouncement(true);
    try {
      await adminSaveAnnouncementBar(items);
      toast.success("Top announcement bar updated — live on every page");
    } catch {
      toast.error("Could not save the announcement bar");
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const saveHero = async () => {
    if (!heroForm.line1.trim() && !heroForm.line2.trim()) return toast.error("Headline cannot be empty");
    setSavingHero(true);
    try {
      await adminSaveHero(heroForm);
      toast.success("Hero banner updated — live on the home page");
    } catch {
      toast.error("Could not save the hero banner");
    } finally {
      setSavingHero(false);
    }
  };

  const saveVideo = async () => {
    setSavingVideo(true);
    try {
      await adminSaveHomeVideo(videoUrl.trim());
      toast.success(videoUrl.trim() ? "Home page video updated" : "Video section hidden (no link set)");
    } catch {
      toast.error("Could not save the video link");
    } finally {
      setSavingVideo(false);
    }
  };

  if (!me) {
    return <main className="min-h-[60vh] flex items-center justify-center text-charcoal/50 text-sm" data-testid="admin-loading">Loading owner panel…</main>;
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.25em] text-xs font-semibold text-gold">R V Ilkal Sarees</p>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-charcoal mt-2">Owner Panel</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setEditing(null); setFormOpen(true); }}
            data-testid="add-saree-btn"
            className="flex items-center gap-2 bg-maroon text-ivory text-xs tracking-[0.15em] uppercase px-5 py-3 hover:bg-maroon-dark transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Saree
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 border border-gold/40 text-charcoal/70 text-xs tracking-[0.15em] uppercase px-5 py-3 hover:border-gold hover:text-maroon transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8" data-testid="admin-stats">
          {[
            { icon: ShoppingBag, label: "Total Orders", value: stats.total_orders, testid: "stat-orders" },
            { icon: IndianRupee, label: "Revenue", value: inr(stats.revenue), testid: "stat-revenue" },
            { icon: Package, label: "Sarees Live", value: stats.products, testid: "stat-products" },
            { icon: Bell, label: "New Orders", value: stats.new_orders, testid: "stat-new-orders" },
            { icon: Bell, label: "Subscribers", value: stats.subscribers ?? 0, testid: "stat-subscribers" },
          ].map((s) => (
            <div key={s.label} className="border border-gold/25 bg-surface/50 p-5 sm:p-6" data-testid={s.testid}>
              <s.icon className="w-5 h-5 text-maroon" strokeWidth={1.5} />
              <p className="font-heading text-2xl sm:text-3xl text-charcoal mt-3">{s.value}</p>
              <p className="text-xs tracking-[0.12em] uppercase text-charcoal/50 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1 mt-10 border-b border-gold/25">
        {[["orders", "Orders"], ["products", "Sarees"], ["subscribers", "Subscribers"], ["offer", "Banners"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`px-6 py-3 text-xs tracking-[0.15em] uppercase transition-colors ${tab === key ? "bg-maroon text-ivory" : "text-charcoal/60 hover:text-maroon"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="mt-6 space-y-4" data-testid="orders-list">
          {orders.length === 0 && (
            <p className="text-center text-charcoal/50 py-16 border border-gold/20" data-testid="orders-empty">No orders yet. New orders will appear here.</p>
          )}
          {orders.map((o) => (
            <div key={o.id} className="border border-gold/25 p-5 sm:p-6" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-heading text-lg text-charcoal">{o.id}</p>
                  <p className="text-xs text-charcoal/50 mt-0.5">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                </div>
                <span className={`text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 ${STATUS_STYLE[o.status] || STATUS_STYLE.placed}`} data-testid={`order-status-${o.id}`}>
                  {o.status}
                </span>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
                <div>
                  <p className="text-xs tracking-[0.12em] uppercase text-charcoal/40">Customer</p>
                  <p className="text-charcoal mt-1">{o.customer.name} · {o.customer.phone}</p>
                  <p className="text-charcoal/60 text-xs mt-0.5">{o.customer.address}, {o.customer.city}, {o.customer.state} – {o.customer.pincode}</p>
                </div>
                <div>
                  <p className="text-xs tracking-[0.12em] uppercase text-charcoal/40">Items</p>
                  <ul className="mt-1 space-y-0.5">
                    {o.items.map((i, idx) => (
                      <li key={idx} className="text-charcoal/80">{i.name} × {i.qty} — {inr(i.price * i.qty)}</li>
                    ))}
                  </ul>
                  <p className="text-charcoal/50 text-xs mt-1">Shipping: {o.shipping === 0 ? "Free" : inr(o.shipping)}{o.cod_charge ? ` + ${inr(o.cod_charge)} COD fee` : ""} · {o.payment_method === "cod" ? "Cash on Delivery" : "Online (Razorpay)"}{o.payment_status === "paid" ? " · PAID" : ""}</p>
                </div>
                <div className="sm:text-right">
                  <p className="text-xs tracking-[0.12em] uppercase text-charcoal/40">Total</p>
                  <p className="font-heading text-2xl text-maroon mt-1">{inr(o.total)}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gold/15">
                <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                  <SelectTrigger className="w-44 border-gold/40 rounded-sm h-10 text-sm" data-testid={`order-status-select-${o.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ivory border-gold/40 rounded-sm">
                    {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-opt-${o.id}-${s}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <a
                  href={waLink(`Namaste ${o.customer.name}! Regarding your order ${o.id} (${inr(o.total)}) from R V Ilkal Sarees…`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`order-whatsapp-${o.id}`}
                  className="text-xs tracking-[0.12em] uppercase text-[#128C4A] border border-[#25D366] px-4 py-2.5 hover:bg-[#25D366]/10 transition-colors"
                >
                  Message Customer
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "products" && (
        <div className="mt-6" data-testid="products-list">
          <div className="grid gap-3">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 border border-gold/25 p-4" data-testid={`admin-product-${p.id}`}>
                <img src={p.images?.[0]} alt={p.name} className="w-14 h-[4.5rem] object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-base text-charcoal truncate">{p.name}</p>
                  <p className="text-xs text-charcoal/50 mt-0.5 capitalize">
                    {p.category} · {p.fabric} · <span className={p.stock_qty != null && p.stock_qty <= 3 ? "text-red-700 font-semibold" : ""} data-testid={`stock-${p.id}`}>Stock: {p.stock_qty == null ? "Unlimited" : p.stock_qty}</span>
                  </p>
                </div>
                <p className="text-maroon font-semibold whitespace-nowrap">{inr(p.price)}</p>
                <div className="hidden sm:flex gap-1.5">
                  {p.is_new && <span className="text-[10px] bg-gold text-charcoal px-2 py-0.5 uppercase tracking-wider">New</span>}
                  {p.is_bestseller && <span className="text-[10px] bg-maroon text-ivory px-2 py-0.5 uppercase tracking-wider">Best</span>}
                  {!p.in_stock && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 uppercase tracking-wider">Out</span>}
                </div>
                <button
                  onClick={() => { setEditing(p); setFormOpen(true); }}
                  data-testid={`edit-product-${p.id}`}
                  className="w-9 h-9 border border-gold/40 flex items-center justify-center text-charcoal/60 hover:text-maroon hover:border-gold"
                  aria-label="Edit"
                >
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => removeProduct(p)}
                  data-testid={`delete-product-${p.id}`}
                  className="w-9 h-9 border border-gold/40 flex items-center justify-center text-charcoal/60 hover:text-red-700 hover:border-red-300"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ProductForm open={formOpen} onOpenChange={setFormOpen} product={editing} onSave={saveProduct} saving={saving} />

      {tab === "subscribers" && (
        <div className="mt-6 -mt-0" data-testid="subscribers-list">
          {subscribers.length === 0 && (
            <p className="text-center text-charcoal/50 py-16 border border-gold/20" data-testid="subscribers-empty">No mobile subscribers yet. Numbers collected from the popup will appear here.</p>
          )}
          <div className="grid gap-2">
            {subscribers.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 border border-gold/25 p-4" data-testid={`subscriber-${i}`}>
                <p className="text-sm text-charcoal font-semibold">+91 {s.phone}</p>
                <p className="text-xs text-charcoal/50">{s.source} · {new Date(s.created_at).toLocaleString("en-IN")}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === "offer" && (
        <div className="mt-6 max-w-2xl space-y-12" data-testid="offer-editor">
          <div>
            <h2 className="font-heading text-xl text-charcoal">Hero Banner (First Screen)</h2>
            <p className="text-sm text-charcoal/60 leading-relaxed mt-2">
              The big headline on the first screen of the home page. Change it anytime — festive lines, sale headlines, anything.
            </p>
            <div className="mt-4 space-y-3">
              <input
                value={heroForm.overline}
                onChange={(e) => setHeroForm({ ...heroForm, overline: e.target.value })}
                data-testid="hero-overline-input"
                placeholder="Small gold line above the headline"
                className="w-full bg-ivory border border-gold/40 focus:border-gold outline-none px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35"
              />
              <input
                value={heroForm.line1}
                onChange={(e) => setHeroForm({ ...heroForm, line1: e.target.value })}
                data-testid="hero-line1-input"
                placeholder="Headline line 1 — e.g. Six Yards of"
                className="w-full bg-ivory border border-gold/40 focus:border-gold outline-none px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35"
              />
              <input
                value={heroForm.line2}
                onChange={(e) => setHeroForm({ ...heroForm, line2: e.target.value })}
                data-testid="hero-line2-input"
                placeholder="Headline line 2 — shown in gold italics, e.g. Living Heritage"
                className="w-full bg-ivory border border-gold/40 focus:border-gold outline-none px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35"
              />
              <textarea
                value={heroForm.subheadline}
                onChange={(e) => setHeroForm({ ...heroForm, subheadline: e.target.value })}
                rows={2}
                data-testid="hero-subheadline-input"
                placeholder="One or two sentences below the headline"
                className="w-full bg-ivory border border-gold/40 focus:border-gold outline-none p-4 text-sm text-charcoal placeholder:text-charcoal/35 leading-relaxed"
              />
            </div>
            <button
              onClick={saveHero}
              disabled={savingHero}
              data-testid="hero-save-btn"
              className="mt-4 bg-maroon text-ivory text-xs tracking-[0.15em] uppercase px-8 py-3.5 hover:bg-maroon-dark transition-colors disabled:opacity-60"
            >
              {savingHero ? "Saving…" : "Save Hero Banner"}
            </button>
          </div>
          <div>
            <h2 className="font-heading text-xl text-charcoal">Home Page Video</h2>
            <p className="text-sm text-charcoal/60 leading-relaxed mt-2">
              Paste a YouTube link or a direct video (.mp4) link. It appears between New Arrivals and the wedding section. Leave empty to hide the video section.
            </p>
            <input
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              data-testid="video-url-input"
              placeholder="https://youtube.com/watch?v=… or https://…/video.mp4"
              className="mt-4 w-full bg-ivory border border-gold/40 focus:border-gold outline-none px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35"
            />
            <button
              onClick={saveVideo}
              disabled={savingVideo}
              data-testid="video-save-btn"
              className="mt-4 bg-maroon text-ivory text-xs tracking-[0.15em] uppercase px-8 py-3.5 hover:bg-maroon-dark transition-colors disabled:opacity-60"
            >
              {savingVideo ? "Saving…" : "Save Video"}
            </button>
          </div>
          <div>
            <h2 className="font-heading text-xl text-charcoal">Top Announcement Bar</h2>
            <p className="text-sm text-charcoal/60 leading-relaxed mt-2">
              Edit the short lines scrolling in the maroon strip at the very top of every page. One line per row.
            </p>
            <textarea
              value={announcementText}
              onChange={(e) => setAnnouncementText(e.target.value)}
              rows={4}
              data-testid="announcement-text-input"
              placeholder={"Authentic Ilkal Sarees\nPan India Delivery\nPremium Handloom Collection"}
              className="mt-4 w-full bg-ivory border border-gold/40 focus:border-gold outline-none p-4 text-sm text-charcoal placeholder:text-charcoal/35 leading-relaxed"
            />
            <button
              onClick={saveAnnouncement}
              disabled={savingAnnouncement}
              data-testid="announcement-save-btn"
              className="mt-4 bg-maroon text-ivory text-xs tracking-[0.15em] uppercase px-8 py-3.5 hover:bg-maroon-dark transition-colors disabled:opacity-60"
            >
              {savingAnnouncement ? "Saving…" : "Save Top Bar"}
            </button>
          </div>
          <div>
            <h2 className="font-heading text-xl text-charcoal">Gold Offer Motion Banner</h2>
            <p className="text-sm text-charcoal/60 leading-relaxed mt-2">
              Edit the lines scrolling in the gold motion banner on the home page. One line per row — they repeat in order.
            </p>
            <textarea
              value={offerText}
              onChange={(e) => setOfferText(e.target.value)}
              rows={6}
              data-testid="offer-text-input"
              placeholder={"Dussehra Festival Offer — Flat 15% OFF\nFree shipping above ₹1,999"}
              className="mt-4 w-full bg-ivory border border-gold/40 focus:border-gold outline-none p-4 text-sm text-charcoal placeholder:text-charcoal/35 leading-relaxed"
            />
            <button
              onClick={saveOffer}
              disabled={savingOffer}
              data-testid="offer-save-btn"
              className="mt-4 bg-maroon text-ivory text-xs tracking-[0.15em] uppercase px-8 py-3.5 hover:bg-maroon-dark transition-colors disabled:opacity-60"
            >
              {savingOffer ? "Saving…" : "Save Offer Banner"}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
